import { Navigate, NavLink, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import {
  Handshake,
  HardDrive,
  KeyRound,
  ListChecks,
  Lock,
  MailPlus,
  Palette,
  SlidersHorizontal,
  UserRound,
  Users,
  type LucideIcon,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAuth } from '@/auth/AuthProvider';
import { PageHeader } from '@/components/ui/page-header';
import { Label } from '@/components/ui/label';
import { AccountSettings } from '@/pages/settings/AccountSettings';
import { GeneralSettings } from '@/pages/settings/GeneralSettings';
import { OptionsSettings } from '@/pages/settings/OptionsSettings';
import { PartnersSettings } from '@/pages/settings/PartnersSettings';
import { UsersSettings } from '@/pages/settings/UsersSettings';
import { ApiTokensSettings } from '@/pages/settings/ApiTokensSettings';
import { DriveSettings } from '@/pages/settings/DriveSettings';
import { TemplatesSettings } from '@/pages/settings/TemplatesSettings';
import { BrandSettings } from '@/pages/settings/BrandSettings';

interface Section {
  path: string;
  label: string;
  icon: LucideIcon;
  /** The API refuses non-admins entirely (users, tokens); other pages are read-only for them. */
  adminOnly?: boolean;
}

const SECTIONS: Section[] = [
  { path: 'general', label: 'General', icon: SlidersHorizontal },
  { path: 'options', label: 'Options', icon: ListChecks },
  { path: 'partners', label: 'Partners', icon: Handshake },
  { path: 'users', label: 'Users & roles', icon: Users, adminOnly: true },
  { path: 'api-tokens', label: 'API tokens', icon: KeyRound, adminOnly: true },
  { path: 'drive', label: 'Google Drive', icon: HardDrive },
  { path: 'templates', label: 'Message templates', icon: MailPlus },
  { path: 'brand', label: 'Brand', icon: Palette },
  { path: 'account', label: 'Account', icon: UserRound },
];

function SettingsNav() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const current =
    SECTIONS.find((s) => pathname === `/settings/${s.path}` || pathname.startsWith(`/settings/${s.path}/`))
      ?.path ?? 'general';

  return (
    <>
      <div className="lg:hidden">
        <Label htmlFor="settings-section" className="sr-only">
          Settings section
        </Label>
        <select
          id="settings-section"
          value={current}
          onChange={(e) => navigate(`/settings/${e.target.value}`)}
          className="flex h-9 w-full rounded-md border border-input bg-background px-3 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
        >
          {SECTIONS.map((s) => (
            <option key={s.path} value={s.path}>
              {s.label}
              {s.adminOnly && !isAdmin ? ' (admins only)' : ''}
            </option>
          ))}
        </select>
      </div>
      <nav aria-label="Settings sections" className="hidden w-56 shrink-0 lg:block">
        <ul className="flex flex-col gap-1">
          {SECTIONS.map((s) => (
            <li key={s.path}>
              <NavLink
                to={`/settings/${s.path}`}
                className={({ isActive }) =>
                  cn(
                    'flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                    isActive ? 'bg-accent text-accent-foreground' : 'text-muted-foreground',
                  )
                }
              >
                <s.icon className="h-4 w-4 shrink-0" aria-hidden="true" />
                <span className="flex-1">{s.label}</span>
                {s.adminOnly ? (
                  <Lock className="h-3 w-3 opacity-60" aria-label="Administrators only" role="img" />
                ) : null}
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>
    </>
  );
}

/**
 * Settings (spec §12). Mounted at `/settings/*`; the sub-pages are nested routes with a left
 * sub-navigation on desktop and a select on small screens. Admin-only sections stay visible to
 * everyone but are read-only (or "administrators only" where the API refuses non-admins).
 */
export function SettingsPage() {
  return (
    <>
      <PageHeader
        title="Settings"
        description="Margins, option lists, users, API tokens, Google Drive, message templates, brand and your account."
      />
      <div className="flex flex-col gap-6 lg:flex-row lg:items-start">
        <SettingsNav />
        <div className="min-w-0 flex-1">
          <Routes>
            <Route index element={<Navigate to="/settings/general" replace />} />
            <Route path="general" element={<GeneralSettings />} />
            <Route path="options" element={<OptionsSettings />} />
            <Route path="partners" element={<PartnersSettings />} />
            <Route path="users" element={<UsersSettings />} />
            <Route path="api-tokens" element={<ApiTokensSettings />} />
            <Route path="drive" element={<DriveSettings />} />
            <Route path="templates" element={<TemplatesSettings />} />
            <Route path="brand" element={<BrandSettings />} />
            <Route path="account" element={<AccountSettings />} />
            <Route path="*" element={<Navigate to="/settings/general" replace />} />
          </Routes>
        </div>
      </div>
    </>
  );
}
